import { DONATION_RECURRENCES } from "@/common/helpers/donationRecurrence";
import { FC, useState, useEffect, useMemo } from "react";

import {
  Column,
  CommunicationPreferenceFormType,
  getField,
  getFilenameFromUrl,
  getOppositeLocale,
  Locales,
  ProductDonationRecurrenceInterval,
  QuestionType,
} from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import { v4 } from "uuid";

import { useLiveFormAmplitudeEvents } from "../../../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../../../common/LiveFormEditorContext";
import { buildDonationEmailTranslationText } from "../helpers/emailTranslationText";
import { swapTranslationContent } from "../helpers/swapTranslationContent";
import { TranslationSwapVersionContext } from "../sharedComponents/TranslationSwapContext";
import { TranslationModalLayout } from "../TranslationModalLayout/TranslationModalLayout";
import { DonationFormTranslationFields, DonationTranslationsData } from "../translation.types";

import { DonationTranslationColumn } from "./DonationTranslationColumn";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { AutoHideDuration } from "@/common/constants/Notistack";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { mapDataToEditChoiceFields, mapDataToEditQuestionFields } from "@/common/helpers/customQuestion";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { QuestionFieldFormData } from "@/common/types/customQuestion";
import {
  EditDonationFormTranslationsInput,
  DonationFormWithUserAmountOutput,
  DonationFormWithUserFieldOutput,
  DonationFormWithUserOrOrgAccessOutput,
  DonationFormQuestionOutput,
} from "@/common/types/trpc";
import { EmailContentsLoadState } from "@/features/LiveFormEditor/common/EmailContentsLoadState";
import { FormEmailContents } from "@/features/LiveFormEditor/common/formEmailContents";
import { useCommunicationPreferencesForPreview } from "@/features/LiveFormEditor/LivePreview/useCommunicationPreferencesForPreview";

interface DonationTranslationModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSave: () => void;
}

const getFormattedQuestionFields = (
  apiDonation: DonationFormWithUserOrOrgAccessOutput,
  locale: Locales
): QuestionFieldFormData[] => {
  return (
    apiDonation?.questions
      ?.filter((question) => question.type !== QuestionType.Name)
      ?.slice()
      ?.sort((a, b) => a.sortIndex - b.sortIndex)
      .map((question) => {
        const field = question.questionFields?.find((questionField) => questionField.locale === locale);
        const isTranslation = locale !== apiDonation?.locale;
        const isNoTranslationSaved = locale !== field?.locale;

        return {
          ...(isTranslation ? {} : field || {}),
          id: isNoTranslationSaved ? v4() : field?.id || v4(),
          body: field?.body || "",
          questionId: question.id,
          choices:
            question?.choices
              ?.slice()
              .sort((choiceA, choiceB) => choiceA.sortIndex - choiceB.sortIndex)
              .map((choice) => ({
                id: choice.id,
                value: choice.choiceFields?.find((choiceField) => choiceField.locale === locale)?.body || "",
                sortIndex: choice.sortIndex,
              })) || [],
        };
      }) || []
  );
};

type TranslationFieldInput = EditDonationFormTranslationsInput["donationFormTranslations"]["fields"];
type QuestionFieldsInput = NonNullable<TranslationFieldInput["questionsFields"]>;
type ChoiceFieldsInput = NonNullable<TranslationFieldInput["choiceFields"]>;
type DonationAmountTranslationInput = TranslationFieldInput["donationFormAmountFields"][number];

const mapDataToCreateTranslationAmount = (
  donationFormAmounts?: DonationFormWithUserAmountOutput[] | null
): DonationAmountTranslationInput[] => {
  if (!donationFormAmounts) {
    return [];
  }
  const oneTimeAmount = donationFormAmounts
    .filter((amount) => amount.recurrenceInterval === ProductDonationRecurrenceInterval.OneTime)
    .map((amount, index) => {
      return {
        message: amount.message || "",
        id: amount.id,
        amount: parseInt(String(amount.amount)),
        isRecurrent: false,
        recurrenceInterval: ProductDonationRecurrenceInterval.OneTime,
        sortIndex: index,
      };
    });

  const recurringAmounts = DONATION_RECURRENCES.filter(({ stripeInterval }) => stripeInterval !== null).flatMap(
    ({ frequency }) =>
      donationFormAmounts
        .filter((amount) => amount.recurrenceInterval === frequency)
        .map((amount, index) => ({
          message: amount.message || "",
          id: amount.id,
          amount: parseInt(String(amount.amount)),
          isRecurrent: true,
          recurrenceInterval: frequency,
          sortIndex: index,
        }))
  );

  return [...oneTimeAmount, ...recurringAmounts];
};

type DonationTranslationFieldData = Omit<DonationFormTranslationFields, "attachment">;

export const buildTranslationField = (
  fieldData: DonationTranslationFieldData,
  localeValue: Locales,
  questions: DonationFormQuestionOutput[] | null | undefined,
  attachmentUrl?: string
): TranslationFieldInput => {
  const mappedQuestions = mapDataToEditQuestionFields(fieldData.questionsFields ?? [], localeValue);
  const mappedChoices = mapDataToEditChoiceFields(
    (fieldData.questionsFields ?? []).flatMap((questionField) => questionField.choices || []),
    (questions ?? []).flatMap((question) => question.choices ?? []),
    localeValue
  );

  const questionFields: QuestionFieldsInput | null =
    mappedQuestions.length > 0 ? (mappedQuestions as QuestionFieldsInput) : null;
  const choiceFields: ChoiceFieldsInput | null = mappedChoices.length > 0 ? (mappedChoices as ChoiceFieldsInput) : null;

  const translationField: TranslationFieldInput = {
    id: fieldData.id,
    title: fieldData.title,
    description: fieldData.description || undefined,
    emailObject: fieldData.emailObject || undefined,
    emailContent: fieldData.emailContent || undefined,
    fundraiserEmailBody: fieldData.fundraiserEmailBody || undefined,
    chequeDescription: fieldData.chequeDescription || undefined,
    autoRenewalEmailObject: fieldData.autoRenewalEmailObject ?? undefined,
    autoRenewalEmailContent: fieldData.autoRenewalEmailContent ?? undefined,
    questionsFields: null,
    choiceFields: null,
    donationFormAmountFields: mapDataToCreateTranslationAmount(fieldData.donationFormAmounts).map((amount) => ({
      id: amount.id,
      amount: amount.amount,
      sortIndex: amount.sortIndex,
      isRecurrent: amount.isRecurrent,
      recurrenceInterval: amount.recurrenceInterval,
      message: amount.message || undefined,
    })),
    emailAttachmentUrl: attachmentUrl ?? undefined,
  };

  if (questionFields) {
    translationField.questionsFields = questionFields;
  }

  if (choiceFields) {
    translationField.choiceFields = choiceFields;
  }

  return translationField;
};

export const DonationTranslationModal: FC<DonationTranslationModalProps> = ({ isOpen, closeModal, onSave }) => {
  const { organization } = useCurrentUserContext();
  const { apiFormObject: apiDonation, optionalFundraiserToken } = useLiveFormEditorContext<"donation-form">();
  const formLocale = apiDonation.locale;
  const translationLocale = getOppositeLocale({ organizationCountry: organization?.country, locale: formLocale });
  const emailContentsQuery = trpc.getDonationAutomatedEmailContents.useQuery(
    { donationFormId: apiDonation.id, fundraiserToken: optionalFundraiserToken },
    { enabled: isOpen }
  );

  if (emailContentsQuery.data) {
    return (
      <DonationTranslationForm
        isOpen={isOpen}
        closeModal={closeModal}
        onSave={onSave}
        emailContents={emailContentsQuery.data}
      />
    );
  }

  return (
    <TranslationModalLayout
      open={isOpen}
      onClose={closeModal}
      onSubmit={(event) => event.preventDefault()}
      onSwap={() => {}}
      formLocale={formLocale}
      translationLocale={translationLocale}
      organizationCountry={organization?.country}
      isTranslating={emailContentsQuery.isPending}
      actionsDisabled
      slots={{
        content: (
          <EmailContentsLoadState
            hasError={emailContentsQuery.isError}
            onRetry={() => void emailContentsQuery.refetch()}
          />
        ),
      }}
    />
  );
};

const DonationTranslationForm: FC<DonationTranslationModalProps & { emailContents: FormEmailContents }> = ({
  isOpen,
  closeModal,
  onSave,
  emailContents,
}) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { apiFormObject: apiDonation, updateApiFormObject, liveFormData } = useLiveFormEditorContext<"donation-form">();
  const { organization } = useCurrentUserContext();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const [isTranslating, setIsTranslating] = useState(false);
  const [swapVersion, setSwapVersion] = useState(0);

  const { mutateAsync: updateDonationTranslations } = trpc.updateDonationFormTranslations.useMutation();
  const utils = trpc.useUtils();
  const { questionTitle: communicationPreferencesTitle } = useCommunicationPreferencesForPreview(
    apiDonation.id,
    CommunicationPreferenceFormType.DonationForm
  );

  const formLocale = apiDonation.locale;
  const translationLocale = getOppositeLocale({ organizationCountry: organization?.country, locale: formLocale });
  const fieldsEmailText = buildDonationEmailTranslationText(emailContents, formLocale);
  const translationsEmailText = buildDonationEmailTranslationText(emailContents, translationLocale);

  const defaultDonationFields = (apiDonation?.donationFormFields?.find(
    (field) => field.locale === apiDonation?.locale
  ) || {}) as NonNullable<DonationFormWithUserFieldOutput>;

  const defaultDonationTranslations = (apiDonation?.donationFormFields?.find(
    (field) => field.locale === translationLocale
  ) || {}) as NonNullable<DonationFormWithUserFieldOutput>;

  const defaultQuestionsFieldsTranslations = useMemo<QuestionFieldFormData[]>(
    () => getFormattedQuestionFields(apiDonation, translationLocale),
    [apiDonation, translationLocale]
  );

  const defaultQuestionsFields = useMemo<QuestionFieldFormData[]>(
    () => getFormattedQuestionFields(apiDonation, apiDonation?.locale),
    [apiDonation]
  );

  const baseDonationFormAmounts = defaultDonationFields.donationFormAmounts?.map((donationFormAmount, index) => ({
    sortIndex: donationFormAmount.sortIndex ?? index,
    isRecurrent: donationFormAmount.isRecurrent,
    recurrenceInterval: donationFormAmount.recurrenceInterval,
    amount: donationFormAmount.amount,
    message: donationFormAmount.message || "",
    id: donationFormAmount.id,
  }));

  const translationDonationFormAmounts = defaultDonationFields.donationFormAmounts?.map(
    (donationFormAmount, index) => ({
      sortIndex: defaultDonationTranslations.donationFormAmounts?.[index]?.sortIndex ?? index,
      isRecurrent: donationFormAmount.isRecurrent,
      recurrenceInterval: donationFormAmount.recurrenceInterval,
      amount: donationFormAmount.amount,
      message: defaultDonationTranslations.donationFormAmounts?.[index]?.message || "",
      id: defaultDonationTranslations.donationFormAmounts?.[index]?.id || v4(),
    })
  );

  const defaultLiveDonationFields = getField(liveFormData?.donation.donationFormFields, formLocale);

  const defaultValues = {
    fields: {
      ...defaultDonationFields,
      ...fieldsEmailText,
      chequeDescription: defaultLiveDonationFields?.chequeDescription || defaultDonationFields.chequeDescription || "",
      questionsFields: defaultQuestionsFields,
      attachment: {
        fileUrl: fieldsEmailText.emailAttachmentUrl,
        filename: getFilenameFromUrl(fieldsEmailText.emailAttachmentUrl),
      },
      donationFormAmounts: baseDonationFormAmounts,
    },
    translations: {
      ...defaultDonationTranslations,
      ...translationsEmailText,
      chequeDescription: defaultDonationTranslations.chequeDescription || "",
      id: defaultDonationTranslations.id || v4(),
      questionsFields: defaultQuestionsFieldsTranslations,
      locale: translationLocale,
      attachment: {
        fileUrl: translationsEmailText.emailAttachmentUrl,
        filename: getFilenameFromUrl(translationsEmailText.emailAttachmentUrl),
      },
      donationFormAmounts: translationDonationFormAmounts,
    },
  };

  const form = useForm<DonationTranslationsData>({
    defaultValues,
    mode: "onChange",
    shouldUnregister: true,
  });

  useEffect(() => {
    if (Object.values(form.formState.errors).length > 0) {
      enqueueSnackbar(t("dashboard", "donationForm.alertMissingTranslations"), { vibe: "danger" });
    }
  }, [enqueueSnackbar, form.formState.errors, t]);

  const requestEditionForm = async () => {
    const submitFormData = form.getValues();
    const { attachment: fieldAttachment, ...fields } = submitFormData.fields;
    const { attachment: translationAttachment, ...translations } = submitFormData.translations;

    const fieldsInput = buildTranslationField(
      fields,
      apiDonation.locale,
      apiDonation.questions,
      fieldAttachment?.fileUrl ?? undefined
    );
    const translationsInput = buildTranslationField(
      translations,
      translationLocale,
      apiDonation.questions,
      translationAttachment?.fileUrl ?? undefined
    );

    const trpcDonationTranslations = {
      fields: {
        ...fieldsInput,
        questionsFields: fieldsInput.questionsFields ?? null,
        choiceFields: fieldsInput.choiceFields ?? null,
      },
      translations: {
        ...translationsInput,
        questionsFields: translationsInput.questionsFields ?? null,
        choiceFields: translationsInput.choiceFields ?? null,
        communicationPreferencesTitle: submitFormData.communicationPreferencesTitle,
      },
    } satisfies EditDonationFormTranslationsInput["donationFormTranslations"];

    return await updateDonationTranslations({
      donationFormId: apiDonation.id,
      donationFormTranslations: trpcDonationTranslations,
    });
  };

  const handleSwapTranslations = () => {
    const { fields, translations } = form.getValues();
    const [swappedFields, swappedTranslations] = swapTranslationContent(fields, translations);

    form.setValue(Column.Fields, swappedFields, { shouldDirty: true });
    form.setValue(Column.Translations, swappedTranslations, { shouldDirty: true });
    setSwapVersion((version) => version + 1);
  };

  const onSubmit = form.handleSubmit(async () => {
    if (!apiDonation) {
      return;
    }

    const { data, error } = await requestEditionForm();

    if (error) {
      enqueueSnackbar(t("dashboard", "donationForm.edit.translations.error"), {
        vibe: "danger",
        autoHideDuration: AutoHideDuration.fast,
      });
    }

    if (data) {
      enqueueSnackbar(t("dashboard", "donationForm.edit.translations"), {
        vibe: "positive",
        autoHideDuration: AutoHideDuration.fast,
      });

      updateApiFormObject(data);
      await utils.getDonationAutomatedEmailContents.invalidate({ donationFormId: apiDonation.id });
      logUserActionEvent(AmplitudeEvents.FormTranslationAdded);
      onSave();
      closeModal();
    }
  });

  return (
    <FormProvider {...form}>
      <TranslationModalLayout
        open={isOpen}
        onClose={closeModal}
        onSubmit={onSubmit}
        onSwap={handleSwapTranslations}
        formLocale={formLocale}
        translationLocale={translationLocale}
        organizationCountry={organization?.country}
        isTranslating={isTranslating}
        isSaving={form.formState.isSubmitting}
        slots={{
          content: (
            <TranslationSwapVersionContext.Provider value={swapVersion}>
              <DonationTranslationColumn
                data={apiDonation}
                defaultChequeDescription={{
                  fields: defaultValues.fields.chequeDescription,
                  translations: defaultValues.translations.chequeDescription,
                }}
                formLocale={formLocale}
                emailText={{ fields: fieldsEmailText, translations: translationsEmailText }}
                translationLocale={translationLocale}
                communicationPreferencesTitle={communicationPreferencesTitle}
                onTranslationStateChange={setIsTranslating}
              />
            </TranslationSwapVersionContext.Provider>
          ),
        }}
      />
    </FormProvider>
  );
};
